import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { LiveAnnouncer } from '@angular/cdk/a11y';

import { PosteDialogComponent } from './poste-dialog/poste-dialog.component';
import { MatConfirmDialogComponent } from 'src/app/shared/mat-confirm-dialog/mat-confirm-dialog.component';
import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { IPoste } from 'src/app/shared/models/poste';
import { PosteService } from './poste.service';

@Component({
  selector: 'app-postes-list',
  templateUrl: './postes-list.component.html',
  styleUrls: ['./postes-list.component.less']
})
export class PostesListComponent implements OnInit, AfterViewInit {

  dataSource = new MatTableDataSource<IPoste>();
  displayedColumns: string[] = ['libelle', 'active', 'actions'];
  loading = false;

  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private posteService: PosteService,
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
    private _liveAnnouncer: LiveAnnouncer
  ) {}

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.loadPostes();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (item, property) => (item as any)[property];
  }

  announceSortChange(sortState: Sort): void {
    if (sortState.direction) {
      this._liveAnnouncer.announce(
        `Tri ${sortState.direction === 'asc' ? 'ascendant' : 'descendant'}`
      );
    } else {
      this._liveAnnouncer.announce('Tri réinitialisé');
    }
  }

  // ── Chargement ────────────────────────────────────────────────────────────

  loadPostes(): void {
    this.loading = true;
    this.posteService.getAll().subscribe({
      next: (data) => {
        this.dataSource.data = data;
        this.loading = false;
      },
      error: (err) => {
        this.snackbarService.openSnackBar(
          this.extractErrorMessage(err, 'Erreur lors du chargement des postes'),
          'error-snack-bar'
        );
        this.loading = false;
      }
    });
  }

  // ── Filtre ────────────────────────────────────────────────────────────────

  applyFilter(event: Event): void {
    this.dataSource.filter = (event.target as HTMLInputElement).value.trim().toLowerCase();
  }

  // ── Dialog ────────────────────────────────────────────────────────────────

  openDialog(poste: IPoste | null = null): void {
    const dialogRef = this.dialog.open(PosteDialogComponent, {
      width: '500px',
      maxWidth: '95vw',
      data: poste
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) this.loadPostes();
    });
  }

  // ── Suppression ───────────────────────────────────────────────────────────

  deletePoste(poste: IPoste): void {
    const dialogRef = this.dialog.open(MatConfirmDialogComponent, {
      width: '400px',
      data: { message: `Supprimer le poste "${poste.libelle}" ?` }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.posteService.delete(poste.posteId).subscribe({
        next: () => {
          this.snackbarService.openSnackBar('Poste supprimé avec succès');
          this.dataSource.data = this.dataSource.data.filter(p => p.posteId !== poste.posteId);
        },
        error: (err) => {
          // 409 = poste utilisé par au moins un collaborateur (CDC §8.1)
          console.error('Erreur lors de la suppression du poste', err);
          const message = err?.status === 409
            ? 'Ce poste est utilisé par au moins un collaborateur, il ne peut pas être supprimé.'
            : this.extractErrorMessage(err, 'Erreur lors de la suppression');
          this.snackbarService.openSnackBar(message, 'error-snack-bar');
        }
      });
    });
  }

  // ── Helper erreurs ────────────────────────────────────────────────────────

  private extractErrorMessage(err: any, fallback: string): string {
    const body = err?.error;
    if (body?.message && typeof body.message === 'string') return body.message;
    if (typeof body === 'string') return body;
    return fallback;
  }
}
